import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { findPlayerByAuthId } from '../lib/api';
import { Player, UserRole, AuthUser } from '../data/types';
import type { User } from '@supabase/supabase-js';

interface AuthContextType {
  user: AuthUser | null;
  supabaseUser: User | null;
  isLoading: boolean;
  isAdmin: boolean;
  isPlayer: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshPlayer: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [supabaseUser, setSupabaseUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const resolveUser = useCallback(async (sbUser: User | null) => {
    if (!sbUser) {
      setUser(null);
      setSupabaseUser(null);
      setIsLoading(false);
      return;
    }

    setSupabaseUser(sbUser);

    // Find linked player record
    const player = await findPlayerByAuthId(sbUser.id);

    let role: UserRole = 'public';
    if (player?.is_admin) {
      role = 'admin';
    } else if (player) {
      role = 'player';
    }

    setUser({
      id: sbUser.id,
      email: sbUser.email ?? '',
      role,
      player: player ?? undefined,
    });

    setIsLoading(false);
  }, []);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      resolveUser(session?.user ?? null);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      resolveUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, [resolveUser]);

  const handleSignIn = async (email: string, password: string) => {
    setIsLoading(true);
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setIsLoading(false);
      throw error;
    }
    await resolveUser(data.user);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSupabaseUser(null);
  };

  const handleRefreshPlayer = async () => {
    const { data: { user: sbUser } } = await supabase.auth.getUser();
    await resolveUser(sbUser);
  };

  const value: AuthContextType = {
    user,
    supabaseUser,
    isLoading,
    isAdmin: user?.role === 'admin',
    isPlayer: user?.role === 'player',
    isAuthenticated: user !== null,
    signIn: handleSignIn,
    signOut: handleSignOut,
    refreshPlayer: handleRefreshPlayer,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
